import { getEnSlug, getSvSlug } from "@/lib/seo/resolver";
import { resolveConfigSlug, resolveConfigEnSlug } from "@/lib/config/registry";

/** Guide slug mappings where SV and EN slugs differ (sv → en). */
const guideSlugMap: Record<string, string> = {
  "app-hamta-skrap": "app-pick-up-junk",
  "tiptapp-goteborg": "tiptapp-gothenburg",
  "vad-ar-rut-avdrag": "what-is-rut-deduction",
  "rut-avdrag-bortforsling": "rut-deduction-junk-removal",
  "rut-avdrag-dodsbostadning": "rut-deduction-estate-clearance",
  "rut-avdrag-mobelmontering": "rut-deduction-furniture-assembly",
  "rut-avdrag-flytt": "rut-deduction-moving",
  "kalkyl-rut-rot": "rut-rot-calculator",
  "rut-avdrag-pensionar": "rut-deduction-pensioners",
};
const guideSlugMapReverse: Record<string, string> = Object.fromEntries(
  Object.entries(guideSlugMap).map(([sv, en]) => [en, sv])
);

/** Static page mappings (sv path ↔ en path, without leading slash) */
const staticPages: Record<string, string> = {
  "om-oss": "en/about-us",
  "jobba-hos-oss": "en/work-with-us",
  "allmanna-villkor": "en/terms-of-service",
  "radera-konto": "en/delete-account",
  "kontakta-oss": "en/contact-us",
  "boka": "en/book",
  "guide": "en/guide",
};
const staticPagesReverse: Record<string, string> = Object.fromEntries(
  Object.entries(staticPages).map(([sv, en]) => [en, sv])
);

/**
 * Given a pathname, return the paired URL for the other locale.
 *
 * Checks config-driven pages first, then falls back to the legacy resolver.
 *
 * Examples:
 *   getAlternateUrl("/bortforsling", "en")                    → "/en/junk-removal"
 *   getAlternateUrl("/tomma-kallare", "en")                   → "/en/basement-clearance"
 *   getAlternateUrl("/en/basement-clearance", "sv")           → "/tomma-kallare"
 *   getAlternateUrl("/om-oss", "en")                          → "/en/about-us"
 *   getAlternateUrl("/en/about-us", "sv")                       → "/om-oss"
 */
export function getAlternateUrl(pathname: string, targetLocale: "sv" | "en"): string {
  const clean = pathname.replace(/^\/+|\/+$/g, "");

  if (targetLocale === "en") {
    if (staticPages[clean]) return "/" + staticPages[clean];

    // Guide pages (may have different SV/EN slugs)
    if (clean.startsWith("guide/")) {
      const svGuideSlug = clean.replace("guide/", "");
      const enGuideSlug = guideSlugMap[svGuideSlug] || svGuideSlug;
      return "/en/guide/" + enGuideSlug;
    }

    // Config-driven pages (sv slug → en slug)
    const config = resolveConfigSlug(clean);
    if (config?.en) return "/en/" + config.en.slug;

    // Legacy resolver
    const enSlug = getEnSlug(clean);
    return enSlug ? "/en/" + enSlug : "/en";
  }

  if (staticPagesReverse[clean]) return "/" + staticPagesReverse[clean];
  const enPath = clean.replace(/^en\//, "");

  // Guide pages (may have different SV/EN slugs)
  if (enPath.startsWith("guide/")) {
    const enGuideSlug = enPath.replace("guide/", "");
    const svGuideSlug = guideSlugMapReverse[enGuideSlug] || enGuideSlug;
    return "/guide/" + svGuideSlug;
  }

  // Config-driven pages (en slug → sv slug)
  const config = resolveConfigEnSlug(enPath);
  if (config) return "/" + config.slug;

  // Legacy resolver
  const svSlug = getSvSlug(enPath);
  return svSlug ? "/" + svSlug : "/";
}
